import React, { ChangeEvent, useEffect, useMemo, useState } from 'react';

import { Button, Checkbox, Input, Radio, Space } from 'antd';
import { CheckboxValueType } from 'antd/es/checkbox/Group';
import _ from 'lodash';

import { updateFilterItemInList } from '../../../../../../../../../helpers/utils/reports';
import { unApi } from '../../../../../../../api/endpoints/reports/unApi';
import { useActions } from '../../../../../../../hooks/useActions';
import { useForceUpdate } from '../../../../../../../hooks/useForceUpdate';
import { useTypedSelector } from '../../../../../../../hooks/useTypedSelector';
import ListTextSelector from '../../../ListTextSelector';

const LazyListPicker = ({
  disabled = false,
  id,
  index,
  showListPlus,
  modalVisibleHandler,
  prefix,
  popoverVisible,
  ...props
}: any) => {
  const forceUpdate = useForceUpdate();

  const { selectedSchool } = useTypedSelector((state) => state.currentSchool);
  const filters = useTypedSelector(
    (state) => state.compare.compareFilters[`compareFilters${prefix}`]
  );
  const { setCompareFilter, setMetaFilterUpdate } = useActions();

  const [ isEditing, setIsEditing ] = useState(false);
  const [ filterMode, setFilterMode ] = useState(1);
  const [ _id, _setId ] = useState(id);

  // Приводим id к строке для безопасной работы с методами строк
  const safeId = String(id ?? '');

  useEffect(() => {
    forceUpdate();
  }, [ props.itemData, index, id ]);

  useEffect(() => {
    let _tempId = safeId;

    if (filterMode === 2) {
      setFilterMode(2);
      if (!safeId.includes('not_')) {
        _tempId = `not_${safeId}`;
      }
    } else {
      setFilterMode(1);
      if (safeId.includes('not_')) {
        _tempId = safeId.split('not_')[1];
      } else {
        _tempId = safeId;
      }
    }

    _setId(_tempId);
  }, [ safeId, filterMode ]);

  useEffect(() => {
    if (safeId.includes('not_')) {
      setFilterMode(2);
    } else {
      setFilterMode(1);
    }
  }, [safeId]);

  const filter = useMemo(() => {
    let _filter = props.itemData ? props.itemData : null;
    if (!props.list) {
      _filter = null;
    } else if (!isEditing) {
      setIsEditing(true);
    }

    return _filter ? _filter : { items: [], checkeds: [], ...props };
  }, [ filters, _id, index, props.list, props.itemData, props ]);

  const [ items, setItems ] = useState(() => {
    // Нормализуем id к строкам для корректного сравнения
    const normalizeItem = (item: any) => ({
      ...item,
      id: item.id?.toString() || '',
    });
    const safeCheckeds = (filter.checkeds || []).map(normalizeItem);
    const safeItems = (filter.items || []).map(normalizeItem);
    return [
      ...safeCheckeds,
      ...safeItems.filter(
        (x: any) => !safeCheckeds.some((y: any) => String(x.id) === String(y.id))
      ),
    ];
  });

  const [ status, setStatus ] = useState('start');
  const [ searchValue, setSearchValue ] = useState('');

  const [ currentData, setCurrentData ] = useState(filter);

  // Сброс состояния при каждом открытии Popover
  useEffect(() => {
    if (popoverVisible) {
      // Сбрасываем поисковую строку и статус
      setSearchValue('');
      setStatus('start');
      
      // Нормализуем id к строкам для корректного сравнения
      const normalizeItem = (item: any) => ({
        ...item,
        id: item.id?.toString() || '',
      });
      
      // В режиме редактирования восстанавливаем данные из itemData
      if (props.itemData && props.list) {
        const safeCheckeds = (props.itemData.checkeds || []).map(normalizeItem);
        const safeItems = (props.itemData.items || []).map(normalizeItem);
        setItems([
          ...safeCheckeds,
          ...safeItems.filter(
            (x: any) => !safeCheckeds.some((y: any) => String(x.id) === String(y.id))
          ),
        ]);
        setCurrentData(props.itemData);
      } else {
        // В режиме создания нового фильтра — начинаем с чистого состояния
        setItems([]);
        setCurrentData({ items: [], checkeds: [], ...props });
      }
    }
  }, [popoverVisible]);

  const onApply = () => {
    let _currentData = {
      ...currentData,
      id: _id,
    };

    if (isEditing) {
      let _filters = _.cloneDeep(filters);

      const payload = { ..._currentData };
      _filters = updateFilterItemInList(_filters, index, payload);

      setCompareFilter({ index: prefix, filters: _filters });
      setMetaFilterUpdate({ value:true });
      modalVisibleHandler(false);

      return;
    }

    const payload = {
      ..._currentData,
      id: _id,
    };

    let _filters = _.cloneDeep(filters);
    _filters.push(payload);

    setCompareFilter({ index: prefix, filters: _filters });
    setMetaFilterUpdate({ value:true });
    modalVisibleHandler(false);
  };

  const loadItems = (value: string) => {
    if (value.length === 0 || !selectedSchool) return;

    setStatus('load');

    unApi
      .getFilterList(parseInt(selectedSchool?.id || '0'), id, value)
      .then(function (response) {
        // Нормализуем id к строкам для корректного сравнения
        const normalizeItem = (item: any) => ({
          ...item,
          id: item.id?.toString() || '',
        });
        
        let data = (response.data || []).map(normalizeItem);
        const safeCheckeds = (filter.checkeds || []).map(normalizeItem);
        
        setItems([
          ...safeCheckeds,
          ...data.filter(
            (x: any) => !safeCheckeds.some((y: any) => String(x.id) === String(y.id))
          ),
        ]);

        if (!data.length) setStatus('loadfalse');
        else setStatus('loadtrue');
      })
      .catch(function (error) {
        setStatus('loadfalse');
      });
  };

  const onChangeCheck = (checkedValue: CheckboxValueType[]) => {
    setCurrentData((prevState: any) => {
      return {
        ...prevState,
        items,
        // Сравниваем как строки для корректной работы с BigInt id
        checkeds: items.filter(({ id }) => checkedValue.some((v) => String(v) === String(id))),
      };
    });
  };

  const onChangeSearch = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value || '';
    setSearchValue(value);

    if (value.trim().length === 0) {
      setItems(filter.checkeds || []);
      setStatus('start');
      return;
    }

    loadItems(value);
  };

  const checkALl = () => {
    setCurrentData((prevState: any) => {
      return {
        ...prevState,
        items,
        checkeds: items,
      };
    });
  };

  const onClear = () => {
    setCurrentData((prevState: any) => {
      return {
        ...prevState,
        checkeds: [],
      };
    });
  };

  // Функция для установки выбранных элементов из ListTextSelector
  // Пустой массив очищает, непустой — заменяет текущие checkeds
  const setCheckeds = (checkeds: any[]) => {
    if (checkeds.length === 0) {
      // Очистка — ничего не делаем, т.к. следом придут реальные данные
      return;
    }
    setCurrentData((prevState: any) => {
      return {
        ...prevState,
        checkeds: checkeds,
      };
    });
  };

  // Функция для установки списка элементов из ListTextSelector
  // Пустой массив очищает, непустой — заменяет текущие items
  const setList = (items: any[]) => {
    if (items.length === 0) {
      // Очистка — сбрасываем items
      setCurrentData((prevState: any) => {
        return {
          ...prevState,
          items: [],
        };
      });
      setItems([]);
      return;
    }
    setCurrentData((prevState: any) => {
      return {
        ...prevState,
        items: items,
      };
    });
    setItems(items);
  };

  return (
    <div style={{ height: 380 }}>
      <Input
        onChange={(e) => onChangeSearch(e)}
        placeholder="Найти"
        allowClear
        disabled={disabled}
      />

      <div style={{ marginTop: 15 }}>
        <Radio.Group
          disabled={disabled}
          onChange={(e) => setFilterMode(e.target.value)}
          value={filterMode}
        >
          <Radio value={1}>Включить</Radio>
          <Radio value={2}>Исключить</Radio>
        </Radio.Group>
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          margin: '10px 0px',
        }}
      >
        <Button disabled={disabled} onClick={checkALl} size="small">
          Выбрать все
        </Button>

        <ListTextSelector
          showListPlus={showListPlus}
          id={id}
          setList={setCheckeds}
          setMainList={setList}
        />

        <Button disabled={disabled} onClick={onClear} size="small">
          Очистить
        </Button>
      </div>

      <div style={{ width: 300, height: 200, overflowY: 'auto' }}>
        {items.length === 0 && status === 'start' && (
          <div style={{ textAlign: 'center' }}>
            Начните вводить в поиске,
            <br />
            чтобы получить данные.
          </div>
        )}
        {status === 'loadfalse' && searchValue.trim().length > 0 && (
          <div style={{ textAlign: 'center' }}>
            По вашему запросу ничего не найдено
          </div>
        )}

        <Checkbox.Group
          disabled={disabled}
          value={currentData.checkeds.map((item: any) => item.id)}
          onChange={onChangeCheck}
        >
          <Space direction="vertical">
            {items.map((item) => (
              <div key={item.id}>
                <Checkbox value={item.id} style={{ marginRight: 7 }}>
                  {item.name.replace(/&nbsp;/g, '\u00A0')}
                </Checkbox>
              </div>
            ))}
          </Space>
        </Checkbox.Group>
      </div>

      <Button
        disabled={disabled}
        type="primary"
        style={{ marginTop: 10 }}
        onClick={onApply}
      >
        Применить
      </Button>
    </div>
  );
};

export default LazyListPicker;
